import { ReactElement, useState } from "react";
import type { GetStaticProps, GetStaticPaths } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import { restApiESGetSettings, restApiESQuery } from "~/utils/restApi";
import PageMargins from "~/components/styled/PageMargins";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import { ListItemNews } from "~/components/content/ListItemNews";
import { Pagination } from "~/components/ui/Pagination";
import { appConfig } from "~/config";
import { ListingInfo } from "~/components/ui/ListingInfo";
import { HeaderWithFilter } from "~/components/partials/HeaderWithFilter";
import {
  NewsFilter,
  FilterData,
  defaultFilterData,
} from "~/components/partials/NewsFilter";
import { InstagramPosts } from "~/components/content/InstagramPosts";

const NewsList = ({ posts, page }: { posts: any; page: number }) => {
  const config = useConfigContext();

  const [filterQuery, setFilterQuery] = useState("");
  const [filterData, setFilterData] = useState<FilterData>({
    ...defaultFilterData,
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  return (
    <>
      <NextHeadSeo
        title={`News ${page > 1 ? `- ${page}` : ``} - ${config.appTitle}`}
      />
      <HeaderWithFilter
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        title="News"
        controlsId="news-filter"
      />
      <main id="content">
        <NewsFilter
          id="news-filter"
          initiallyOpen={false}
          {...{
            isFilterOpen,
            filterData,
            setFilterData,
            filterQuery,
            setFilterQuery,
          }}
        />

        <div id="listing" />
        {posts?.data?.length > 0 && (
          <>
            {page > 1 && (
              <ListingInfo>
                You are on page <b>{page}</b> of <b>{posts?.pageTotal}</b> of a
                listing of <b>{posts?.postTotal}</b> news posts.
              </ListingInfo>
            )}

            <PageMargins
              spaceTop={page === 1 ? 6 : 0}
              spaceBottom={posts?.pageTotal && posts?.pageTotal > 1 ? 0 : 5}
            >
              {posts?.data.map((post: any, index: number) => (
                <ListItemNews
                  post={post}
                  key={`post-${index + 1}`}
                  featured={page === 1 && index === 0}
                />
              ))}
            </PageMargins>
            {page &&
              posts?.postTotal &&
              posts?.pageTotal &&
              posts?.pageTotal > 1 && (
                <PageMargins spaceBottom={4}>
                  <Pagination
                    firstPageShowMore="Show more news"
                    {...{
                      page,
                      postTotal: posts?.postTotal,
                      pageTotal: posts?.pageTotal,
                    }}
                    paginationBaseUrl={`${config.baseUrl}/news/`}
                  />
                </PageMargins>
              )}
          </>
        )}
        <InstagramPosts />
      </main>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [
    "/news/",
    "/news/2",
    "/news/3",
    "/news/4",
    "/news/5",
    "/news/6",
    "/news/7",
  ],
  fallback: "blocking",
});

export const getStaticProps: GetStaticProps = async (context) => {
  const page = context?.params?.page
    ? Array.isArray(context?.params?.page) && context?.params?.page?.length
      ? parseInt(
          context?.params?.page[context?.params?.page.length - 1] as string
        )
      : parseInt(context?.params?.page as string)
    : 1;

  const posts = await restApiESQuery({
    type: "post",
    perPage: appConfig.postsPerPage,
    offset: page > 1 ? (page - 1) * appConfig.postsPerPage : undefined,
    page,
    orderby: "post_date",
    order: "desc",
  });

  const frontendSettings = await restApiESGetSettings(["category"]);
  if (!posts?.data?.length)
    return {
      props: {
        frontendSettings,
      },
      notFound: true,
      revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
    };

  return {
    props: {
      frontendSettings,
      posts,
      page,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("newsListing")
      : 15,
  };
};

NewsList.getLayout = function getLayout(page: ReactElement) {
  return <LayoutMain>{page}</LayoutMain>;
};
export default NewsList;
